import { config } from '../config';
import { isAirportCode, stripLegLabel } from '../lib/bookingShorthand';
import { getAuthToken, getTenant } from './client';
import { ApiError, unwrap } from './envelope';

/**
 * Direct browser → dispatch-be client for the booking flow. The console's ID
 * token IS a dispatch admin token (sign-in goes through dispatch's
 * /admin/login), so calls carry the agent's own identity — dispatch attributes
 * the job to them exactly as if it came from the dashboard.
 *
 * Dispatch speaks the same `{status, code, message, data}` envelope dialect as
 * the CRM (precedent: auth/dispatchAuth.ts), with one twist: its invalid-token
 * code is 1035, which the CRM deliberately never uses. A dispatch auth bounce
 * gets one forced token refresh and a replay, and NEVER signs the agent out of
 * the console — the CRM session may be perfectly fine.
 */

export const DISPATCH_PATHS = {
  jobCreate: '/admin/job/create',
  /**
   * Parks a booking nobody has finished yet. NOT a job endpoint: it stores an
   * opaque jsonb blob dispatch never reads inside, so the draft payload is
   * built separately from `JobPayload` — see booking/draftPayload.ts.
   */
  draftCreate: '/admin/draftJob/create',
  vehicleTypes: '/admin/vehicle/list/ids-names',
  /**
   * The customer's saved Stripe cards, `/:customerId` appended. Guarded on
   * dispatch's own `customer_payment_cards` slug — an agent without it gets a
   * 1036 here while the rest of the booking form works, so the caller must
   * degrade to the no-card UI rather than fail the booking.
   */
  customerCards: '/admin/stripe/payment-methods',
  googleTimezone: '/admin/google/timezone',
  /** Authenticated variant that also zone-checks; its 1001 message is user-facing. */
  googleDetailV2: '/admin/google/detail/v2',
  // The two /google/* routes are dispatch's UNAUTHENTICATED global proxy —
  // same routes the dashboard hits without a token.
  googleSearch: '/google/search',
  googleDetail: '/google/detail',
  /**
   * FCM topic membership. Dispatch owns it for both clients — the standalone
   * console subscribes its own web-push token to the tenant admin topic the
   * CRM pushes to (lib/webNotify.ts). It sits off the API ROOT, not under
   * /admin, and it DOES require the ID token despite the dashboard reaching it
   * through its "global" (unauthenticated) tunnel — that tunnel attaches the
   * token unconditionally, so a bare call answers 1034.
   */
  subscribeTopics: '/notification/subscribe-to-topics',
} as const;

/** Matches the dashboard's Dio timeouts; these are quick lookups, not probes. */
const TIMEOUT_MS = 30_000;

// ---- Wire types -------------------------------------------------------------

/** A place suggestion row from /google/search. */
export interface AddressSuggestion {
  placeId: string;
  text: string;
  addressType: 'N' | 'A' | string;
}

/** The resolved address from /google/detail — camelCase placeId INBOUND. */
export interface PlaceDetail {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  placeId: string;
  latitude: number;
  longitude: number;
  addressType: 'N' | 'A' | string;
  zoneId?: number | null;
}

/** The address block of a job leg — snake_case place_id OUTBOUND only. */
export interface DispatchAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  place_id: string;
  latitude: number;
  longitude: number;
  addressType: 'N' | 'A' | string;
  zoneId: number | null;
}

export interface JobLegOtherData {
  addressId: null;
  /** P pickup · S stop · D dropoff. */
  type: 'P' | 'S' | 'D';
  /**
   * Always '' — the dashboard's 'create' sentinel demands a matching
   * flightData block we never have. The human-readable flight number goes in
   * flightIata, the airline in airlineName.
   */
  flightNumber: '';
  airPortCode: string;
  isFbo: false;
  flightIata: string;
  airlineName: string;
  /** UTC ISO; present on the PICKUP leg only. */
  pickUpTimeDate: string | null;
  waitTime: null;
  timeIn: string | null;
  arriveAirportAt: 'At pickup time';
  /** 0 pickup · 1..n stops · 100 dropoff. */
  sequence: number;
}

export interface JobLeg {
  address: DispatchAddress;
  otherData: JobLegOtherData;
}

export interface JobPassenger {
  id: 0;
  customerId?: number;
  /** Single concatenated "First Last" on the wire. */
  name: string;
  email: string;
  phone: string;
}

export interface JobNote {
  /** String ids on the wire: '1' customer note, '3' chauffeur note. */
  id: '1' | '3';
  value: string;
}

export interface JobPayloadData {
  isSendNotification: boolean;
  /**
   * The BOOKER, and exactly one of these two carries it — `job_form.dart:235`
   * sends the same `accountNumber` as `affiliateId` when the booker is an
   * affiliate and as `customerId` when it is a customer, both null for a guest.
   * Not a farm-out: that is a different field on a different screen.
   */
  affiliateId: number | null;
  customerId: number | null;
  /** What the affiliate is paid. The CRM cannot price a farm-out, so always 0. */
  affiliateValue: 0;
  vehicleTypeId: number;
  /** 'O' one-way · 'H' hourly (legacy 'F' exists — never send it). */
  type: 'O' | 'H';
  duration: number | null;
  passengerCount: number | null;
  luggageCount: number | null;
  notes: [JobNote, JobNote];
  source: 'A';
  bookedName: string;
  bookedEmail: string;
  bookedPhone: string;
  billingName: '';
  billingCompanyName: '';
  voucher: '';
  timeZone?: string;
  /**
   * The affiliate's OWN reservation number for this trip. Affiliate bookings
   * only — `job_form.dart:253` omits the key entirely otherwise, and so do we.
   */
  requesterResNo?: string;
}

/**
 * How the job gets paid. dispatch-be lowercases this and compares it to the
 * literal `'paymentlink'` (`commonApis/jobcreate.js`) — the snake_case
 * `payment_link` it names in its own error string is REJECTED, so this camel
 * spelling is load-bearing.
 */
export type JobPaymentMethod = 'card' | 'paymentLink';

export interface JobPayload {
  isQuote: boolean;
  /** Null only when `isBilling` or `isQuote` — dispatch 400s otherwise. */
  paymentMethod: JobPaymentMethod | null;
  /** The Stripe `pm_…` id; required by dispatch when method is `card`. */
  paymentMethodId: string | null;
  /** True = bill the customer later, which is what makes payment optional. */
  isBilling: boolean;
  uid: '';
  data: JobPayloadData;
  pickUp: JobLeg;
  dropOff: JobLeg;
  stopOff: JobLeg[];
  primaryPassenger: JobPassenger | Record<string, never>;
  secondaryPassenger: Record<string, never>;
  addOns: [];
}

export interface VehicleTypeItem {
  id: number;
  name: string;
  passengerCapacity?: number;
  luggageCapacity?: number;
  isActive?: boolean;
}

/**
 * One saved card. Dispatch deep-camelCases Stripe's payload on the way out
 * (`stripeCont.makeRes`), so `exp_month` arrives as `expMonth`.
 */
export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
}

// ---- Draft (a parked booking) -----------------------------------------------

/**
 * `POST /admin/draftJob/create` stores `jobData` as opaque jsonb and never
 * reads a field of it, so the shape below is the dispatch DASHBOARD's — it is
 * mirrored field for field from its `models/job/draft_job_data.dart`, because
 * the dashboard is what reopens a draft in Add Job. Its decoder is key by key
 * with a default for every field, which cuts both ways: an unknown key is
 * ignored rather than rejected, and a misspelt one loses that value silently.
 *
 * Two deliberate absences, both the dashboard's rules: there is no `isQuote`
 * (job or quote is decided by the button pressed when the draft is converted),
 * and no payment block (a Stripe token has no business sitting in a blob for a
 * month, and may be detached by the time the draft reopens).
 */
export interface DraftAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  /** 'N' normal · 'A' airport. */
  addressType: string;
  latitude: number;
  longitude: number;
  /** camelCase here — a JOB leg spells the same field `place_id`. */
  placeId: string;
  zoneId: number | null;
}

/** Which of Add Job's Address / Airport / FBO tabs owns the leg. */
export type DraftLocationKind = 'address' | 'airport' | 'fbo';

export interface DraftJobLeg {
  address: DraftAddress;
  /** P pickup · S stop · D dropoff. */
  type: 'P' | 'S' | 'D';
  locationKind: DraftLocationKind;
  /** 0 pickup · 1..n stops · 100 dropoff, as on the job wire. */
  seq: number;
  addressId: number | null;
  airportCode: string;
  /** The airline on an airport leg — the job wire's `airlineName`. */
  airlineIata: string;
  /** The flight number on an airport leg — the job wire's `flightIata`. */
  flightIata: string;
  /** Always '' — the `create` sentinel belongs to a job save, not a draft. */
  flightNumber: string;
  /** A matched FlightAware record; the CRM never looks one up. */
  flight: null;
  /** UTC ISO, pickup leg only. */
  timeIn: string | null;
  arriveAirportAt: string;
  waitTimeMinutes: number | null;
  sameAsPickup: boolean;
}

/** First and last split, unlike a job's single concatenated `name`. */
export interface DraftJobPassenger {
  id: number;
  customerId: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/**
 * The dashboard's CustomerSuggestItem, carried whole so a reopened draft shows
 * the booker's name instead of an id. `priority` and `isBillingAccount` are not
 * readable from the CRM and take the dashboard's own defaults; the only cost is
 * that Add Job withholds "bill to account" until the agent re-picks the
 * customer, and payment is asked for at conversion regardless.
 */
export interface DraftJobCustomer {
  id: number;
  name: string;
  email: string;
  phone: string;
  priority: number;
  isBillingAccount: boolean;
  note: string;
}

/**
 * The dashboard's `AffiliateSuggestItem`, minus the three fields crm_readonly
 * cannot see. `priority`, `isBilling` and `gnetId` take Add Job's own defaults
 * when the draft is reopened there — the same trade `DraftJobCustomer` above
 * already makes, and for the same reason.
 */
export interface DraftJobAffiliate {
  id: number;
  companyName: string;
  email: string;
  phone: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
}

export interface DraftJobAccount {
  type: 'guest' | 'customer' | 'affiliate';
  /** The customer or affiliate id — a job payload's `customerId`/`affiliateId`. */
  accountNumber: number | null;
  customer: DraftJobCustomer | null;
  affiliate: DraftJobAffiliate | null;
  affiliateValue: number;
}

export interface DraftJobTrip {
  type: 'O' | 'H';
  hours: number | null;
  passengerCount: number | null;
  luggageCount: number | null;
  vehicleTypeId: number;
  /**
   * The catalogue row verbatim. Not redundant with the id: Add Job disables its
   * passenger and luggage inputs until it has the row, and `/draftJob/all`
   * searches the stringified blob, so the stored name is what makes searching
   * the drafts list for "Sedan" find anything.
   */
  vehicle: VehicleTypeItem | null;
}

export interface DraftJobAddOns {
  childSeats: string[];
  tripProtection: boolean;
  travelWithPet: boolean;
  meetAndGreet: boolean;
  meetAndGreetType: 'inside' | 'curbside';
  meetAndGreetText: string;
}

export interface DraftJobBooked {
  name: string;
  email: string;
  phone: string;
  billingName: string;
  billingCompanyName: string;
}

export interface DraftJobData {
  /** Bumped by the dashboard only when defaults cannot absorb a change. */
  version: 1;
  /** `addJob` / `vehicleRates` are the dashboard's; a CRM draft says `crm`. */
  source: string;
  savedAt: string;
  /** A rates-panel fare. The CRM prices nothing, so always null. */
  quotedFare: number | null;
  sendNotification: boolean;
  /** IANA zone of the pickup, '' when the lookup found none. */
  timezone: string;
  voucher: string;
  requesterResNo: string;
  account: DraftJobAccount;
  trip: DraftJobTrip;
  primaryPassenger: DraftJobPassenger;
  secondaryPassenger: DraftJobPassenger;
  pickup: DraftJobLeg;
  dropOff: DraftJobLeg;
  stops: DraftJobLeg[];
  addOns: DraftJobAddOns;
  booked: DraftJobBooked;
  notes: { customer: string; driver: string };
  /**
   * CRM provenance, ignored by the dashboard's decoder. Worth the two keys:
   * `/draftJob/all?search=` matches the stringified blob, so a dispatcher can
   * pull up every draft one conversation produced.
   */
  crmConversationId: number;
  crmSource: true;
}

export interface DraftJobCreateBody {
  jobData: DraftJobData;
  /** UTC ISO; null parks it forever. 30 days is the dashboard's default. */
  expiredAt: string | null;
}

// ---- Transport --------------------------------------------------------------

interface DispatchRequest {
  method?: 'GET' | 'POST';
  body?: unknown;
  query?: Record<string, string>;
  authed?: boolean;
}

async function dispatchRaw(path: string, options: DispatchRequest, forceToken: boolean) {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.authed) {
    const { token, tenant } = await getAuthToken(forceToken);
    headers.Authorization = `Bearer ${token}`;
    headers.tenant = tenant;
  } else {
    // The unauthenticated /google/* proxy still scopes by tenant header — but
    // must never block on (or fail with) token acquisition it doesn't need.
    headers.tenant = getTenant();
  }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const query = options.query ? `?${new URLSearchParams(options.query)}` : '';
  let response: Response;
  try {
    response = await fetch(`${config.dispatchBase}${path}${query}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError('network', 0, 'Dispatch did not answer in time — try again.');
    }
    throw new ApiError('network', 0, 'Could not reach dispatch. Check your connection.');
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(
      'badShape',
      0,
      response.ok ? 'Unexpected response from dispatch.' : `Dispatch error (${response.status}).`,
    );
  }
  return body;
}

/** Dispatch invalid-token (1035) or expired-token (1034): one refresh, one replay. */
const isDispatchAuthBounce = (e: unknown) =>
  e instanceof ApiError && (e.code === 1035 || e.code === 1034);

async function dispatchCall<T>(path: string, options: DispatchRequest = {}): Promise<T> {
  try {
    return unwrap<T>(await dispatchRaw(path, options, false));
  } catch (e) {
    if (options.authed && isDispatchAuthBounce(e)) {
      // Deliberately NOT onAuthExpired: a dispatch bounce says nothing about
      // the CRM session, and signing the agent out mid-booking would be worse
      // than surfacing the error.
      return unwrap<T>(await dispatchRaw(path, options, true));
    }
    throw e;
  }
}

// ---- Client -----------------------------------------------------------------

export interface DispatchClient {
  /** Random per-resolution token, reused search → detail (Google billing session). */
  newSessionToken: () => string;
  searchPlaces: (input: string, sessionToken: string) => Promise<AddressSuggestion[]>;
  getPlaceDetail: (placeId: string, sessionToken: string) => Promise<PlaceDetail>;
  /** Authenticated variant: also zone-checks — rejections throw with dispatch's own words. */
  getPlaceDetailV2: (
    placeId: string,
    sessionToken: string,
    applyOn: 'O' | 'H',
  ) => Promise<PlaceDetail>;
  getTimezone: (latitude: number, longitude: number, dateUtcIso: string) => Promise<string | null>;
  listVehicleTypes: () => Promise<VehicleTypeItem[]>;
  /** Saved cards for a dispatch customer. Throws on 1036 (no card permission). */
  listCustomerCards: (customerId: number) => Promise<SavedCard[]>;
  createJob: (payload: JobPayload) => Promise<{ id: string }>;
  /** Parks the booking; the id is a DRAFT id, not a job id. */
  createDraft: (body: DraftJobCreateBody) => Promise<{ id: string }>;
  /** Subscribe an FCM registration token to push topics. Throws on failure. */
  subscribeFcmTopics: (token: string, topics: string[]) => Promise<void>;
}

export function makeDispatchClient(): DispatchClient {
  return {
    newSessionToken: () => `st_${Date.now().toString(16)}_${Math.random().toString(16).slice(2, 10)}`,
    // No type filter to pass: dispatch's proxy forwards only input/language/
    // session to Google (`commonApis/google.js`) and drops anything else, so
    // narrowing to airports has to happen over the rows that come back.
    searchPlaces: async (input, sessionToken) => {
      const rows = await dispatchCall<AddressSuggestion[]>(DISPATCH_PATHS.googleSearch, {
        method: 'POST',
        body: {
          flag: true,
          input,
          includedRegionCodes: ['us'],
          languageCode: null,
          sessionToken,
        },
      });
      return Array.isArray(rows) ? rows : [];
    },
    getPlaceDetail: (placeId, sessionToken) =>
      dispatchCall<PlaceDetail>(DISPATCH_PATHS.googleDetail, {
        method: 'POST',
        body: { flag: true, placeId, sessionToken },
      }),
    getPlaceDetailV2: (placeId, sessionToken, applyOn) =>
      dispatchCall<PlaceDetail>(DISPATCH_PATHS.googleDetailV2, {
        method: 'POST',
        body: { placeId, sessionToken, applyOn },
        authed: true,
      }),
    getTimezone: async (latitude, longitude, dateUtcIso) => {
      const data = await dispatchCall<{ timeZoneId?: string }>(DISPATCH_PATHS.googleTimezone, {
        query: { latitude: String(latitude), longitude: String(longitude), date: dateUtcIso },
        authed: true,
      });
      return data?.timeZoneId || null;
    },
    listVehicleTypes: async () => {
      const rows = await dispatchCall<VehicleTypeItem[]>(DISPATCH_PATHS.vehicleTypes, {
        authed: true,
      });
      return Array.isArray(rows) ? rows : [];
    },
    listCustomerCards: async (customerId) => {
      const data = await dispatchCall<{ paymentMethods?: unknown }>(
        `${DISPATCH_PATHS.customerCards}/${customerId}`,
        { authed: true },
      );
      const rows = Array.isArray(data?.paymentMethods) ? data.paymentMethods : [];
      return rows
        .map((row) => row as Record<string, unknown>)
        .filter((row) => typeof row.id === 'string')
        .map((row) => {
          // A non-card payment method has no `card` block; the endpoint is
          // asked for `type=card` by default, so treat a missing one as blank
          // rather than dropping a method the agent can still select.
          const card = (row.card ?? {}) as Record<string, unknown>;
          return {
            id: row.id as string,
            brand: typeof card.brand === 'string' ? card.brand : 'card',
            last4: typeof card.last4 === 'string' ? card.last4 : '',
            expMonth: typeof card.expMonth === 'number' ? card.expMonth : null,
            expYear: typeof card.expYear === 'number' ? card.expYear : null,
          };
        });
    },
    createJob: async (payload) => {
      const data = await dispatchCall<{ id: string | number }>(DISPATCH_PATHS.jobCreate, {
        method: 'POST',
        body: payload,
        authed: true,
      });
      return { id: String(data.id) };
    },
    createDraft: async (body) => {
      // Answers with the whole stored record; only its id is of any use here —
      // the CRM never reopens a draft, the dashboard's Drafts list does.
      const data = await dispatchCall<{ id: string | number }>(DISPATCH_PATHS.draftCreate, {
        method: 'POST',
        body,
        authed: true,
      });
      return { id: String(data.id) };
    },
    subscribeFcmTopics: async (token, topics) => {
      await dispatchCall<unknown>(DISPATCH_PATHS.subscribeTopics, {
        method: 'POST',
        body: { token, topics },
        authed: true,
      });
    },
  };
}

// ---- Free-text resolution ---------------------------------------------------

/** The wire address block from a resolved detail (place_id goes snake_case OUT). */
export const toWireAddress = (detail: PlaceDetail): DispatchAddress => ({
  addressLine1: detail.addressLine1 || '',
  addressLine2: detail.addressLine2 || '',
  city: detail.city || '',
  state: detail.state || '',
  postalCode: detail.postalCode || '',
  country: detail.country || '',
  place_id: detail.placeId || '',
  latitude: detail.latitude,
  longitude: detail.longitude,
  addressType: detail.addressType === 'A' ? 'A' : 'N',
  zoneId: detail.zoneId ?? null,
});

export interface ResolvedPlace {
  detail: PlaceDetail;
  /** The suggestion label that matched — shown as the resolved-address hint. */
  matchedText: string;
}

/**
 * Silently resolve free text (an AI-extracted address) to one place — the
 * dashboard's FirstMatchPlaceResolver ported: search, prefer a label-matching
 * row, then detail. Returns null when the search finds nothing; throws when the
 * lookup itself failed (network, zone rejection) — the two must stay
 * distinguishable, only the second is worth retrying.
 */
export async function resolveFirstMatch(
  dispatch: DispatchClient,
  text: string,
  applyOn?: 'O' | 'H',
): Promise<ResolvedPlace | null> {
  const query = stripLegLabel(text);
  if (!query) return null;
  const sessionToken = dispatch.newSessionToken();
  const results = await dispatch.searchPlaces(query, sessionToken);
  if (!results.length) return null;
  const q = query.toLowerCase();
  // "EWR" on its own asks for the airport, not for whatever street shares those
  // three letters — and Google ranks a plain three-letter query by popularity,
  // so the airport is often not the first row.
  const airport = isAirportCode(query)
    ? results.find((r) => r.addressType === 'A')
    : undefined;
  const match = airport ?? results.find((r) => r.text.toLowerCase().includes(q)) ?? results[0];
  const detail = applyOn
    ? await dispatch.getPlaceDetailV2(match.placeId, sessionToken, applyOn)
    : await dispatch.getPlaceDetail(match.placeId, sessionToken);
  return { detail, matchedText: match.text };
}
